import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Skeleton,
  Grid,
  Tooltip
} from '@mui/material';
import { LogisticsObjectMetadata } from '@/api/bol/models';
import type { LogisticsObjectEntity } from '@/types/logisticsObject';
import { createTraceDisplayProps } from '@/utils/grafanaUtils';

interface LogisticsObjectHeaderProps {
  logisticsObjectType: LogisticsObjectEntity;
  objectId: string;
  metadata: LogisticsObjectMetadata | null;
  loading?: boolean;
}

const LogisticsObjectHeader: React.FC<LogisticsObjectHeaderProps> = ({
  logisticsObjectType: _logisticsObjectType,
  objectId: _objectId,
  metadata,
  loading = false
}) => {
  const renderTraceValue = (traceparent?: string) => {
    const traceProps = createTraceDisplayProps(traceparent || '');

    return (
      <Tooltip title={traceProps.title}>
        <Typography 
          variant="body2"
          aria-disabled={!traceProps.onClick}
          sx={{ 
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
            ...traceProps.style,
            '&:hover': traceProps.onClick ? { color: 'primary.dark' } : undefined
          }}
          onClick={traceProps.onClick}
        >
          {traceparent || 'N/A'}
        </Typography>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Card 
        elevation={0}
        sx={{ 
          mb: 2, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Skeleton variant="text" width={160} height={14} />
            <Skeleton variant="text" width={160} height={14} />
            <Skeleton variant="text" width={160} height={14} />
            <Skeleton variant="text" width={160} height={14} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      elevation={0}
      sx={{ 
        mb: 2, 
        border: '1px solid', 
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Metadata Section */}
        {metadata ? (
          <Box sx={{ py: 1.5, px: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Owner */}
              <Grid size={{xs: 12, sm:6 , md:3}}>
                <Box sx={{ height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                    Owner
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {metadata.memberIdentifier}
                  </Typography>
                </Box>
              </Grid>

              {/* Created */}
              <Grid size={{xs: 12, sm:6 , md:3}}>
                <Box sx={{ height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                    Created
                  </Typography>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', minWidth: 20, flexShrink: 0 }}>
                        by
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        {metadata.createdBy}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', minWidth: 20, flexShrink: 0 }}>
                        at
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        {metadata.createdAt ? new Date(metadata.createdAt).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Modified */}
              <Grid size={{xs: 12, sm:6 , md:3}}>
                <Box sx={{ height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                    Modified
                  </Typography>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', minWidth: 20, flexShrink: 0 }}>
                        by
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        {metadata.updatedBy}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', minWidth: 20, flexShrink: 0 }}>
                        at
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        {metadata.updatedAt ? new Date(metadata.updatedAt).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Traces */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                    Traces
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {/* Creation Trace */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', minWidth: 48, flexShrink: 0 }}>
                        CREATE
                      </Typography>
                      {renderTraceValue(metadata.creationTraceparent || undefined)}
                    </Box>
                    
                    {/* Update Trace */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', minWidth: 48, flexShrink: 0 }}>
                        UPDATE
                      </Typography>
                      {renderTraceValue(metadata.updateTraceparent || undefined)}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ py: 1.5, px: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No metadata available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LogisticsObjectHeader;